import * as React from "react";

import { ChevronDown, ChevronRight } from "@/shared/foundation/icons";

import type { TreeRowPlacement } from "../treeRows";

import styles from "./TreeCell.module.css";

/** Indent of a level-0 row's first cell, and each level below it, in px. */
const BASE_INDENT = 12;
const LEVEL_INDENT = 24;
/**
 * The deepest level that indents further. Rows below it line up with it, so
 * a long chain of groups can't push names out of the column; the row's level
 * is still announced.
 */
const MAX_INDENT_DEPTH = 8;

/**
 * A tree column's cell: the row's indent, a group's chevron, and the
 * column's own content. A group's open state goes on the cell as well as its
 * row: keyboard focus lands on cells, never rows, and a screen reader says
 * the state of what has focus, so the cell is where "expanded" and
 * "collapsed" are heard, and heard again as they change.
 */
export function TreeCell({
  placement,
  cell,
  children,
}: {
  placement: TreeRowPlacement;
  cell: HTMLElement | undefined;
  children: React.ReactNode;
}) {
  const expanded =
    placement.kind === "group" ? String(placement.isExpanded) : null;
  React.useLayoutEffect(() => {
    if (!cell) {
      return;
    }
    if (expanded === null) {
      cell.removeAttribute("aria-expanded");
    } else {
      cell.setAttribute("aria-expanded", expanded);
    }
  }, [cell, expanded]);

  return (
    <div
      className={styles.treeCell}
      style={{
        paddingLeft:
          BASE_INDENT +
          Math.min(placement.depth, MAX_INDENT_DEPTH) * LEVEL_INDENT,
      }}
    >
      <span className={styles.chevronSlot} aria-hidden="true">
        {placement.kind === "group" &&
          (placement.isExpanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          ))}
      </span>
      <span className={styles.content}>
        {children}
        {placement.continued && (
          <span className={styles.continued}>continued</span>
        )}
      </span>
    </div>
  );
}
